import React, {
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import logo from "../../styles/images/logo_snotes.png";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { StyleSettings } from "../../styles/StyleSettings";
import { Autocomplete, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { global, useMenuStore } from "@shared/libs/Global";
import FcmNotificationBell from "../push/FcmNotificationBell";
import "./Header.css";
import { showPopupModal } from "@widgets/CommonModal";
import ChangePwdPopup from "@widgets/commonpopup/ChangePwdPopup";
import { UseAuthService } from "@shared/hooks/useAuthService";

interface HeaderProps {
  path: string;
  changeMenuWidth: (isMenuVisible: boolean) => void;
  openDashBoard: () => void;
  width: number;
  onMenuClick: (
    menuKey: string,
    menuTitle: string,
    cd_module: string,
    filePath: string,
    data: any,
    menuPath: string
  ) => void;
  /** SNotes AI Bot 서랍 열기. 없으면 버튼을 그리지 않는다 (2026-09-15 주석 해제) */
  onChatbotOpen?: () => void;
}

/**
 * path_arr을 이용해 전체 경로 텍스트로 변환
 * @param menuItems
 */
const getFullPath = (menuItems: any[]) => {
  if (menuItems) {
    for (let menuItem of menuItems) {
      // cd_menu_parent가 SN_REC 또는 SN_FAV이면 skip
      if (
        menuItem.cd_menu_parent === "SN_REC" ||
        menuItem.cd_menu_parent === "SN_FAV"
      ) {
        continue;
      }

      if (menuItem.fg_menu !== "MGM") {
        if (Array.isArray(menuItem.path_arr)) {
          const nm_path = menuItem.path_arr.map((code: string) => {
            const menu = menuItems.find((item) => item.cd_menu === code);
            return menu ? menu.nm_menu : "";
          });
          menuItem.nm_path = nm_path.join(" > ");
        } else {
          // path_arr이 없는 경우 대비
          menuItem.nm_path = "";
        }
      }
    }
  }
  return menuItems;
};

/**
 * 트리 변환
 * @param items
 */
const buildTree = (items: any[]) => {
  // 최근메뉴, 북마크는 제외
  const filtered = items.filter(
    (item) =>
      item.cd_menu_parent !== "SN_REC" && item.cd_menu_parent !== "SN_FAV"
  );

  const map = new Map(
    filtered.map((item) => [item.cd_menu, { ...item, children: [] }])
  );
  const roots: any[] = [];

  map.forEach((item) => {
    if (item.cd_menu_parent && map.has(item.cd_menu_parent)) {
      map.get(item.cd_menu_parent).children.push(item);
    } else {
      roots.push(item);
    }
  });

  return roots;
};

/**
 * 검색 필터링(트리 순회)
 */
const searchTree = (nodes: any[], keyword: string, results: any[] = []) => {
  const lowerKeyword = keyword.toLowerCase();

  for (const node of nodes) {
    // 경로(nm_path)가 없는 항목은 목록에 빈 줄로 보이므로 제외
    if (
      node.nm_path &&
      (node.nm_path.toLowerCase().includes(lowerKeyword) ||
        node.cd_menu?.toLowerCase().includes(lowerKeyword))
    ) {
      results.push(node);
    }
    if (node.children?.length) {
      searchTree(node.children, keyword, results);
    }
  }
  return results;
};

const Header: React.FC<HeaderProps> = ({
  path,
  changeMenuWidth,
  openDashBoard,
  width,
  onMenuClick,
  onChatbotOpen,
}) => {
  const [menuVisible, setMenuVisible] = useState(true);
  const fontSize = StyleSettings.font;
  const { menuItems } = useMenuStore();

  // 메뉴 검색 관련
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const options: any[] = (getFullPath(menuItems) ?? []).filter(
    (item: any) =>
      item.fg_menu !== "MGM" &&
      item.cd_menu_parent !== "SN_REC" &&
      item.cd_menu_parent !== "SN_FAV"
  );
  const [selectedOption, setSelectedOption] = useState<any | null>(null);
  const { userInfo, config } = global();
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [MAS0121, setMAS0121] = useState(false);

  const { logout } = UseAuthService();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };

    if (config && config.MAS0121 && config.MAS0121.dc_ctrl) setMAS0121(true);
  }, []);

  const treeData = useMemo(() => buildTree(menuItems), [menuItems]);

  if (!userInfo) return null;

  // 아바타에 표시할 이름/이니셜
  const displayName =
    userInfo.fg_user === "2" ? userInfo.nm_user : userInfo.nm_emp;
  const userInitial = (displayName || "").trim().charAt(0) || "U";

  /**
   * 로고 클릭하여 LeftMenu 접기/펴기 메서드
   */
  const toggleMenuVisibility = () => {
    setMenuVisible((prevState) => {
      const newState = !prevState;
      changeMenuWidth(newState); // 부모의 adjustMenuWidth 함수 호출
      return newState;
    });
  };

  /**
   * 검색창 드롭다운에서 값 선택 시 실행될 메서드
   * @param e 이벤트
   * @param value 선택한 메뉴 데이터
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleChange = (e: SyntheticEvent, value: any, reason: string) => {
    if (reason === "selectOption") {
      if (value) {
        openMenu(value); // 같은 메뉴여도 무조건 실행
      }
    }
  };

  /** 메뉴코드로 메뉴 항목을 찾아 연다(권한상 메뉴 목록에 없으면 무시) */
  const openMenuByCode = (cdMenu: string) => {
    const menu = (menuItems ?? []).find(
      (item: any) =>
        item.cd_menu === cdMenu &&
        item.cd_menu_parent !== "SN_REC" &&
        item.cd_menu_parent !== "SN_FAV"
    );
    if (menu) openMenu(menu);
  };

  const openMenu = (value: any) => {
    const { nm_menu, nm_file, cd_menu, cd_module } = value;
    const nm_path = value.nm_path;
    const filePath = `./${cd_module}/${nm_file}/${nm_file}`.toUpperCase();
    onMenuClick(cd_menu, nm_menu, cd_module, filePath, value, nm_path);
  };

  /**
   * 검색어 입력 or x 버튼 클릭 시 실행될 메서드
   * @param e 이벤트
   * @param value 입력한 검색어
   * @param reason 메서드 동작 원인
   */
  const handleInputChange = (
    e: SyntheticEvent,
    value: string,
    reason: string
  ) => {
    switch (reason) {
      case "input":
        setInputValue(value);
        break;
      case "clear":
        setInputValue("");
        break;
    }
  };

  /**
   * x 버튼 클릭 시 동작할 메서드
   */
  const handleClear = () => {
    setInputValue("");
    setSelectedOption(null);
  };

  const clickChangePwd = async () => {
    const passwordPopup = <ChangePwdPopup />;
    const result = await showPopupModal({
      title: "비밀번호 변경",
      children: passwordPopup,
      width: "350px",
      height: "350px",
    });

    if (result !== undefined) {
      if (result === "close") {
        return;
      }
    }

    return;
  };

  return (
    <header
      className={`h-header bg-gradient-to-r bg-white text-black flex items-center shadow-md`}
      style={{ fontSize: fontSize }}
    >
      {/* 왼쪽 영역: 로고와 경로 */}
      <div className="flex items-center h-full">
        {/* 로고 */}
        <div
          className={`flex items-center justify-between h-full`}
          style={{ backgroundColor: "rgb(36 35 198)", width: `${width}px` }}
        >
          <img
            className="logo"
            style={{ width: "230px", height: "60px" }}
            src={logo}
            alt="SmartNotes"
            onClick={toggleMenuVisibility}
          />
        </div>

        {/* 경로 */}
        <div className="flex items-center">
          <button
            className="ml-3 mr-2 text-black"
            onClick={() => {
              openDashBoard();
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: "2rem" }} />
          </button>
          <button
            className="mr-2"
            title="워크플로우"
            onClick={() => openMenuByCode("SN_SYS_WKF_REP_001")}
          >
            <img
              src="/images/icon_workflow.svg"
              alt="워크플로우"
              style={{ width: 24, height: 24 }}
            />
          </button>
          <button
            className="mr-3"
            title="대시보드"
            onClick={() => openMenuByCode("SN_SYS_DSH_REP_001")}
          >
            <img
              src="/images/icon_dashboard.svg"
              alt="대시보드"
              style={{ width: 24, height: 24 }}
            />
          </button>
          <span className="font-bold" style={{ fontSize: fontSize + 1 }}>
            {path || "홈"}
          </span>
        </div>
      </div>

      {/* 검색창 */}
      <div className="flex-1 flex justify-end px-4">
        <div className="w-full max-w-[440px]">
          <Autocomplete
            value={selectedOption}
            selectOnFocus={false}
            disableClearable={true}
            blurOnSelect
            options={options}
            isOptionEqualToValue={() => false}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            getOptionLabel={(option) =>
              `[${option.cd_menu ?? ""}]${option.nm_path ?? ""}`
            }
            filterOptions={(options, state) => {
              if (!state.inputValue.trim()) return [];
              return searchTree(treeData, state.inputValue); // 트리 순서 기반 검색 결과
            }}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            // 드롭다운 폭은 검색창 폭에 맞추고(기본 동작), 항목이 길면 가로 스크롤(하단 스크롤바)
            ListboxProps={{ sx: { overflowX: "auto" } }}
            renderOption={(props, option) => {
              const lowerInput = inputValue.toLowerCase();

              // 검색어와 일치하는 부분을 강조 표시(메뉴코드/경로 공용)
              const highlight = (text?: string) => {
                if (!text) return "";
                const startIndex = text.toLowerCase().indexOf(lowerInput);
                if (startIndex === -1) return text;
                return (
                  <>
                    {text.substring(0, startIndex)}
                    <strong style={{ color: "#0b0c69" }}>
                      {text.substring(
                        startIndex,
                        startIndex + inputValue.length
                      )}
                    </strong>
                    {text.substring(startIndex + inputValue.length)}
                  </>
                );
              };

              return (
                <li
                  {...props}
                  style={{
                    fontSize: fontSize,
                    whiteSpace: "nowrap",
                    width: "max-content",
                    maxWidth: "none",
                  }}
                >
                  {highlight(option.nm_path)}
                </li>
              );
            }}
            renderInput={(params) => (
              <div className="h-9">
                <TextField
                  {...params}
                  inputRef={inputRef}
                  placeholder="메뉴, 기능, 문서 검색"
                  className="h-full w-full"
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      height: "100%",
                      borderRadius: "24px",
                      fontSize: fontSize,
                      backgroundColor: "#ffffff",
                      paddingRight: "10px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e5e7eb",
                    },
                    "& .MuiInputBase-input": {
                      height: "100%",
                      padding: "0 0 0 10px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: fontSize + 1,
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {inputValue && (
                          <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ padding: 0 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#9aa2b6",
                            border: "1px solid #e2e5ec",
                            borderRadius: "6px",
                            padding: "1px 6px",
                            marginLeft: "6px",
                            whiteSpace: "nowrap",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          ⌘K
                        </span>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              </div>
            )}
            forcePopupIcon={false}
          />
        </div>
      </div>

      {/* 오른쪽 영역: 쪽지/미결재, AI Bot, 알림, 사용자 */}
      <div className="flex items-center gap-3 mr-5">
        {MAS0121 && (
          <div className="flex">
            {/* 쪽지 */}
            <button className="flex items-center p-2 text-black font-bold space-x-2">
              <img
                src="/images/b_message.svg"
                className="w-6"
                alt="쪽지 아이콘"
              />
              <span>쪽지</span>
            </button>

            {/* 결재 */}
            <button className="flex items-center p-2 text-black font-bold space-x-2 mr-2">
              <img
                src="/images/b_approval.svg"
                className="w-7"
                alt="미결재 아이콘"
              />
              <span>미결재</span>
            </button>
          </div>
        )}

        {/* SNotes AI Bot — 서랍으로 연다 (2026-09-15 주석 해제) */}
        {onChatbotOpen && (
          <button
            className="flex items-center gap-1.5 text-black font-bold"
            /* 챗봇 고정을 끈 상태의 "밖을 누르면 닫기" 에서 뺀다 — 이 버튼은 스스로 열고 닫는다 (2026-10-22) */
            data-chatbot-toggle="true"
            type="button"
            onClick={onChatbotOpen}
          >
            <AutoAwesomeRoundedIcon
              sx={{ fontSize: "1.25rem", color: "#6b7bff" }}
            />
            <span>SNotes AI Bot</span>
          </button>
        )}

        <span className="h-6 w-px bg-gray-200" />

        {/* 알림: FCM 수신 시 빨간 점, 클릭하면 알림 패널 */}
        <FcmNotificationBell onMenuClick={onMenuClick} />

        <span className="h-6 w-px bg-gray-200" />

        {/* 사용자 */}
        <div
          className="relative"
          style={{ fontSize: fontSize - 1 }}
          ref={popoverRef}
        >
          {/* 프로필 영역 */}
          <div
            className="flex items-center gap-2 cursor-pointer profile-container"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {/* 아바타 (이름 이니셜) */}
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 700,
                background: "linear-gradient(135deg, #5b7bfb 0%, #7b5cff 100%)",
                flexShrink: 0,
              }}
            >
              {userInitial}
            </div>
            {userInfo.fg_user === "2" ? (
              <div className="font-bold leading-tight">
                <div>{userInfo.nm_user || "홍길동"}</div>
              </div>
            ) : (
              <div className="font-bold leading-tight">
                <div>{userInfo.nm_emp || "홍길동"}</div>
                <div className="font-normal text-gray-500">
                  {`${userInfo.nm_biz} ${userInfo.nm_dept}`}
                </div>
              </div>
            )}
            <KeyboardArrowDownRoundedIcon
              sx={{ fontSize: "1.2rem", color: "#9aa2b6" }}
            />
          </div>

          {/* 프로필 말풍선 */}
          {isOpen && (
            <div className="popover-menu" id="popoverMenu">
              <ul>
                {/*<li>내 정보 수정</li>*/}
                <li onClick={clickChangePwd}>비밀번호 수정</li>
                <li onClick={logout}>로그아웃</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
